import * as React from "react";
import type { ConnectProto } from "@racoon/api";
import { isBinValid, isRequiredBinValid } from "@racoon/lib";
import {
  useListCounterpartiesQuery,
  useListOrganizationsQuery,
} from "~/shared/api/organization/organization.grpc";
import {
  findCustomerBinDuplicate,
  findOrganizationBinDuplicate,
  isArchivedCounterparty,
} from "~/shared/lib/bin-duplicate";

type Organization = ConnectProto.Organization;

/** How long the BIN has to sit still before it is looked up. */
const BIN_CHECK_DEBOUNCE_MS = 400;

type BinDuplicate = {
  duplicate?: Organization;
  pending: boolean;
  /** The lookup failed, so the BIN has no verdict — a save must not take that as a pass. */
  failed: boolean;
};

/**
 * The debounced side of a BIN lookup, shared by both lookups below. Both lookups refetch on
 * every subscribe: a cached "no match" may predate an organization created since, here or in
 * another browser, and no cache tag can know about the latter. `pending` holds Save meanwhile.
 */
function useSettledBin(bin: string, skip: boolean) {
  const trimmedBin = bin.trim();
  // The BIN the lookup is actually asking about — `bin` once it stops changing.
  const [binQuery, setBinQuery] = React.useState("");
  React.useEffect(() => {
    const timer = setTimeout(() => setBinQuery(trimmedBin), BIN_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedBin]);

  const skipQuery = skip || binQuery === "" || !isBinValid(binQuery);
  return {
    binQuery,
    skipQuery,
    /** Whether the lookup's answer is about the BIN as it is typed now. */
    current: !skipQuery && binQuery === trimmedBin,
    /** True while the typed BIN has no verdict yet — a save waits rather than races it. */
    pending: (isFetching: boolean) =>
      !skip && isRequiredBinValid(trimmedBin) && (binQuery !== trimmedBin || isFetching),
  };
}

/**
 * The provider's customer that already holds `bin`, looked up as the BIN is typed.
 *
 * A BIN belongs to exactly one customer, so the clash is caught before the save rather than
 * by an error after it. Only the provider's own counterparties are visible here: a customer
 * of another provider slips through, and refusing that one is the backend's job.
 *
 * Archived customers are among them — ListCounterparties without a status answers every
 * contract status — and `archived` tells the caller to point at restoring one instead.
 */
export function useCustomerBinDuplicate({
  providerId,
  bin,
  excludeOrgId,
  skip = false,
}: {
  providerId?: number | null;
  bin: string;
  /** The organization being edited, which holds its own BIN. */
  excludeOrgId?: Organization["id"];
  skip?: boolean;
}): BinDuplicate & { archived: boolean } {
  const { binQuery, skipQuery, current, pending } = useSettledBin(bin, skip || !providerId);
  // ListCounterparties' search covers the BIN.
  const { data, isFetching, isError } = useListCounterpartiesQuery(
    { organizationId: BigInt(providerId ?? 0), search: binQuery },
    { skip: skipQuery, refetchOnMountOrArgChange: true },
  );
  const match = React.useMemo(
    () =>
      current
        ? findCustomerBinDuplicate(data?.counterparties ?? [], binQuery, excludeOrgId)
        : undefined,
    [current, binQuery, data, excludeOrgId],
  );
  return {
    duplicate: match?.organization,
    archived: match != null && isArchivedCounterparty(match),
    pending: pending(isFetching),
    failed: current && isError,
  };
}

/**
 * The top-level organization of `type` that already holds `bin`, across every organization —
 * for the superadmin, who sees them all. The same company may be both a provider and a
 * customer, so organizations of another type never clash.
 */
export function useOrganizationBinDuplicate({
  type,
  bin,
  excludeOrgId,
  skip = false,
}: {
  type?: ConnectProto.OrganizationType | null;
  bin: string;
  /** The organization being edited, which holds its own BIN. */
  excludeOrgId?: Organization["id"];
  skip?: boolean;
}): BinDuplicate {
  const { binQuery, skipQuery, current, pending } = useSettledBin(bin, skip || type == null);
  // ListOrganizations' search covers the BIN; with no list filter it answers every match.
  const { data, isFetching, isError } = useListOrganizationsQuery(
    { type: type ?? undefined, searchQuery: binQuery },
    { skip: skipQuery, refetchOnMountOrArgChange: true },
  );
  const duplicate = React.useMemo(
    () =>
      current
        ? findOrganizationBinDuplicate(data?.organizations ?? [], binQuery, excludeOrgId)
        : undefined,
    [current, binQuery, data, excludeOrgId],
  );
  return { duplicate, pending: pending(isFetching), failed: current && isError };
}
